import React, { useRef } from 'react';
import { Upload, X, FileText, CheckCircle2 } from 'lucide-react';

interface StepDocumentsProps {
    files: {
        rg_cnh_frente: File | null;
        rg_cnh_verso: File | null;
        conta_luz: File[];
        doc_vinculo: File | null;
        procuracao: File | null;
    };
    onFileChange: (field: string, file: any) => void;
    onBack: () => void;
    onFinish: () => void;
    loading: boolean;
}

const FileUploadField: React.FC<{
    label: string;
    fieldKey: string;
    file: File | null;
    onChange: (file: File | null) => void;
    optional?: boolean;
}> = ({ label, fieldKey, file, onChange, optional = true }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            onChange(e.target.files[0]);
        }
    };

    return (
        <div className="mb-4">
            <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                {label} {optional && <span className="text-gray-400 dark:text-slate-500 font-normal normal-case">(Opcional)</span>}
            </label>

            {!file ? (
                <div
                    onClick={() => inputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 dark:border-slate-700 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors group"
                >
                    <Upload size={24} className="text-gray-400 dark:text-slate-500 group-hover:text-primary transition-colors mb-2" />
                    <span className="text-sm text-gray-500 dark:text-slate-400 font-medium">Clique para selecionar</span>
                    <span className="text-xs text-gray-400 dark:text-slate-500">PDF, JPG ou PNG</span>
                </div>
            ) : (
                <div className="border border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/10 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 overflow-hidden">
                        <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg text-green-600 dark:text-green-500">
                            <FileText size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 dark:text-slate-200 truncate">{file.name}</p>
                            <p className="text-xs text-green-600 dark:text-green-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                    </div>
                    <button
                        onClick={() => onChange(null)}
                        className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 rounded-full transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
            />
        </div>
    );
};

const MultiFileUploadField: React.FC<{
    label: string;
    fieldKey: string;
    files: File[];
    onChange: (files: File[]) => void;
    optional?: boolean;
}> = ({ label, fieldKey, files, onChange, optional = true }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            onChange([...files, ...newFiles]);
        }
    };

    const handleRemove = (indexToRemove: number) => {
        onChange(files.filter((_, index) => index !== indexToRemove));
    };

    return (
        <div className="mb-4">
            <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                {label} {optional && <span className="text-gray-400 dark:text-slate-500 font-normal normal-case">(Opcional)</span>}
            </label>

            <div className="space-y-3">
                {files.map((file, index) => (
                    <div key={index} className="border border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/10 rounded-xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 overflow-hidden">
                            <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg text-green-600 dark:text-green-500">
                                <FileText size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 dark:text-slate-200 truncate">{file.name}</p>
                                <p className="text-xs text-green-600 dark:text-green-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                        </div>
                        <button
                            onClick={() => handleRemove(index)}
                            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 rounded-full transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>
                ))}

                <div
                    onClick={() => inputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 dark:border-slate-700 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors group"
                >
                    <Upload size={24} className="text-gray-400 dark:text-slate-500 group-hover:text-primary transition-colors mb-2" />
                    <span className="text-sm text-gray-500 dark:text-slate-400 font-medium">
                        {files.length > 0 ? 'Adicionar mais' : 'Clique para selecionar'}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-slate-500">PDF, JPG ou PNG</span>
                </div>
            </div>

            <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
            />
        </div>
    );
};

const StepDocuments: React.FC<StepDocumentsProps> = ({ files, onFileChange, onBack, onFinish, loading }) => {
    return (
        <div className="animate-fade-in">
            <div className="text-center mb-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100">Anexar Documentos</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">Envie os documentos necessários para análise (opcional).</p>
            </div>

            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">

                {/* Section 1: Documentos Pessoais */}
                <div>
                    <h4 className="flex items-center gap-2 text-sm font-bold text-primary mb-3 pb-1 border-b border-gray-100 dark:border-slate-700/50">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs">1</span>
                        Documentos Pessoais
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FileUploadField
                            label="RG ou CNH (Frente)"
                            fieldKey="rg_cnh_frente"
                            file={files.rg_cnh_frente}
                            onChange={(f) => onFileChange('rg_cnh_frente', f)}
                        />
                        <FileUploadField
                            label="RG ou CNH (Verso)"
                            fieldKey="rg_cnh_verso"
                            file={files.rg_cnh_verso}
                            onChange={(f) => onFileChange('rg_cnh_verso', f)}
                        />
                    </div>
                </div>

                {/* Section 2: Conta de Luz */}
                <div>
                    <h4 className="flex items-center gap-2 text-sm font-bold text-primary mb-3 pb-1 border-b border-gray-100 dark:border-slate-700/50">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs">2</span>
                        Últimas Contas de Luz
                    </h4>
                    <MultiFileUploadField
                        label="Contas de Energia Recente"
                        fieldKey="conta_luz"
                        files={files.conta_luz || []}
                        onChange={(f) => onFileChange('conta_luz', f)}
                    />
                </div>

                {/* Section 3: Vínculo */}
                <div>
                    <h4 className="flex items-center gap-2 text-sm font-bold text-primary mb-3 pb-1 border-b border-gray-100 dark:border-slate-700/50">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs">3</span>
                        Se não for o titular da conta
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FileUploadField
                            label="Documento de Vinculação"
                            fieldKey="doc_vinculo"
                            file={files.doc_vinculo}
                            onChange={(f) => onFileChange('doc_vinculo', f)}
                            optional
                        />
                        <FileUploadField
                            label="Procuração"
                            fieldKey="procuracao"
                            file={files.procuracao}
                            onChange={(f) => onFileChange('procuracao', f)}
                            optional
                        />
                    </div>
                </div>

            </div>

            <div className="flex justify-between mt-6 pt-4 border-t border-gray-100 dark:border-slate-700/50">
                <button
                    type="button"
                    onClick={onBack}
                    className="px-6 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                    Voltar
                </button>
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        onFinish();
                    }}
                    disabled={loading}
                    className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        <>Salvando...</>
                    ) : (
                        <>Concluir Cadastro <CheckCircle2 size={18} /></>
                    )}
                </button>
            </div>
        </div>
    );
};

export default StepDocuments;
